import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { get } from 'lodash'
import { parseISO, format } from 'date-fns'
import { Text, Box, getPaletteColor } from 'pcln-design-system'
import deprecatedPropType from './helpers/deprecatedPropType/deprecatedPropType'
import {
  endDateDelta,
  fullDateChanged,
  hoveredDateDelta,
  executeWhenIsNewHoverDate,
  selectableDateDelta,
  startDateDelta,
  startsWithSunday,
} from './helpers/shouldDayUpdate'

const Cell = styled(Box)`
  display: inline-block;
  vertical-align: top;
  line-height: 1.8;
  text-align: center;
  cursor: pointer;
  color: ${getPaletteColor('border.base')};
  outline-offset: -2px;
  ${(props) => {
    if (!props.valid)
      return `
      cursor: default;
    `
    // This block is for arrow-key navigation, denotes current 'hovered' date (i.e. focused via keys)
    if (props.isCurrentHoverDate && !props.selected)
      return `
      color: ${props.theme.colors.black};
      outline: 1px solid ${getPaletteColor('primary.base')(props)};

      &:hover {
        background: ${props.isTouch ? 'transparent' : getPaletteColor('primary.light')(props)};
      }
    `
    if (!props.grouped && !props.selected)
      return `
      color: ${getPaletteColor('text.dark')(props)};

      &:focus {
        outline: 1px solid ${getPaletteColor('primary.base')(props)};
      }
      &:hover {
        background: ${props.isTouch ? 'transparent' : getPaletteColor('primary.light')(props)};
        outline: 1px solid ${getPaletteColor('primary.base')(props)};
      }
    `
    if (props.grouped && !props.selected)
      return `
      color: ${getPaletteColor('text.dark')(props)};
      background: ${getPaletteColor('primary.light')(props)};
      outline: 1px dotted ${getPaletteColor('primary.light')(props)};
    `
    if (props.selected)
      return `
      color: ${getPaletteColor('text.lightest')(props)};
      background: ${getPaletteColor('primary.base')(props)};
      border-color: ${getPaletteColor('primary.base')(props)};
      outline: 1px dashed ${getPaletteColor('primary.base')(props)};

      &:focus {
        outline: 2px solid ${getPaletteColor('primary.base')(props)};
        color: ${getPaletteColor('text.base')(props)};
        background: ${getPaletteColor('primary.light')(props)};
      }
    `
  }}
`

const stringOrNumber = PropTypes.oneOfType([PropTypes.string, PropTypes.number])

class Day extends React.Component {
  static propTypes = {
    startDate: stringOrNumber,
    endDate: stringOrNumber,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    startsWithSunday: deprecatedPropType('locale'),
    locale: PropTypes.string,
    showHoveredDates: PropTypes.bool,
    onDayClicked: PropTypes.func,
    onDayHovered: PropTypes.func,
    dayWrapperStyleOverride: PropTypes.object,
    highlightedDates: PropTypes.array,
    hoveredDate: PropTypes.string,
    ariaLabel: PropTypes.string,
    fullDate: PropTypes.string,
    width: stringOrNumber,
    isTouch: PropTypes.bool,
    customDayProps: PropTypes.object,
    renderDayFn: PropTypes.func,
    setSelectedDayRef: PropTypes.func,
    maxSelectableDate: PropTypes.string,
    didUseKeyNavigation: PropTypes.bool,
  }

  static defaultProps = {
    width: 1 / 7,
    setSelectedDayRef: () => {},
  }

  constructor(props) {
    super(props)
    this.cellRef = React.createRef()
  }

  componentDidMount() {
    if (Number(this.props.fullDate) === Number(this.props.hoveredDate)) {
      this.props.setSelectedDayRef(this.cellRef)
    }
  }

  /**
   * Most important part of the Calendar performance re-write (2.0.0)
   * Only update this component if it falls within the delta of start dates, end dates, or hover dates
   * or if the day has a new day map information
   */
  shouldComponentUpdate(nextProps) {
    const { fullDate, startDate, endDate, hoveredDate, maxSelectableDate } = this.props
    const {
      startDate: nextStartDate,
      endDate: nextEndDate,
      hoveredDate: nextHoveredDate,
      maxSelectableDate: nextMaxSelectableDate,
    } = nextProps

    if (startsWithSunday(this.props.startsWithSunday, nextProps.startsWithSunday)) return true
    if (fullDateChanged(fullDate, nextProps.fullDate)) return true
    if (!fullDate) return false

    const inStartDateDelta = startDateDelta(startDate, nextStartDate, fullDate, nextEndDate)
    const inEndDateDelta = endDateDelta(endDate, nextEndDate, fullDate, nextStartDate)
    const inHoveredDateDelta = hoveredDateDelta(hoveredDate, nextHoveredDate, fullDate)
    const inSelectableDateDelta = selectableDateDelta(maxSelectableDate, nextMaxSelectableDate, fullDate)

    executeWhenIsNewHoverDate(hoveredDate, nextHoveredDate, fullDate, () => {
      this.cellRef.focus()
      this.props.setSelectedDayRef(this.cellRef)
    })

    const hasNewcustomDayProps = this.props.customDayProps !== nextProps.customDayProps

    return (
      inStartDateDelta ||
      inEndDateDelta ||
      inHoveredDateDelta ||
      inSelectableDateDelta ||
      hasNewcustomDayProps
    )
  }

  getContextualAriaLabel = (isDateFocusable, price) => {
    const { ariaLabel, startDate, endDate } = this.props

    // Only the Day that can be focused needs the verbose label because it's the only one that
    // will be read by screen readers.
    if (!isDateFocusable) {
      return ariaLabel
    }

    const readableStart = startDate ? format(parseISO(startDate), 'PP') : ''
    const readableEnd = endDate ? format(parseISO(endDate), 'PP') : ''

    const departStr = startDate && `Depart ${readableStart}`
    const returnStr = endDate && `Return ${readableEnd}`
    const priceStr = price ? `. Price $${price}` : ''

    const endDateLabel = `${departStr}${returnStr ? `, ${returnStr}` : ''}`
    return endDateLabel
      ? `${endDateLabel}${priceStr}${isDateFocusable ? '. Use arrow keys to change.' : ''}`
      : ariaLabel
  }

  render() {
    const {
      startDate,
      endDate,
      minDate,
      maxDate,
      maxSelectableDate,
      showHoveredDates,
      onDayClicked,
      onDayHovered,
      dayWrapperStyleOverride,
      highlightedDates,
      hoveredDate,
      fullDate,
      width,
      customDayProps,
      renderDayFn,
      didUseKeyNavigation,
    } = this.props

    if (!fullDate) return <Cell py={2} width={width} />

    const currentDate = parseInt(fullDate)
    const isStartDate = startDate && fullDate === startDate
    const isEndDate = endDate && fullDate === endDate
    const isAfterStart = startDate && fullDate > startDate
    const isBeforeEnd = endDate && currentDate < endDate
    const isHovered = hoveredDate && !endDate && currentDate <= hoveredDate
    const isValid = fullDate <= maxDate && fullDate >= minDate && fullDate <= maxSelectableDate
    const isSelected = isStartDate || isEndDate
    const isGrouped = isAfterStart && (isBeforeEnd || isHovered)
    const autobotId = `DATEPICKER_DAY_${fullDate}${!isValid ? '_INVALID' : ''}${
      isSelected ? '_SELECTED' : ''
    }${isGrouped ? '_GROUPED' : ''}`
    const day = fullDate && fullDate.substr(6).replace(/^0/, '')

    const combinedCustomDayProps = {
      day,
      isValid,
      isSelected,
      isGrouped,
      customDayProps,
      fullDate,
      highlightedDates,
    }

    const customDayComponent = typeof renderDayFn === 'function' ? renderDayFn(combinedCustomDayProps) : null
    const isCustomDayAvailable = customDayComponent && React.isValidElement(customDayComponent)

    const price = get(combinedCustomDayProps, 'customDayProps.price', '')

    return (
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events, jsx-a11y/click-events-have-key-events
      <div
        aria-label={this.getContextualAriaLabel(isStartDate || isEndDate, price)}
        aria-pressed={(isValid && isSelected) || ''}
        role='button'
        className='day-cell'
        tabIndex={(!hoveredDate && fullDate === minDate) || fullDate === hoveredDate ? '0' : '-1'}
        onClick={isValid && onDayClicked ? (event) => onDayClicked(event, fullDate) : null}
        onMouseOver={
          isValid && showHoveredDates && onDayHovered ? (event) => onDayHovered(event, fullDate) : null
        }
        ref={(x) => (this.cellRef = x)}
        style={
          dayWrapperStyleOverride || {
            display: 'inline-block',
            width: `${width * 100}%`,
            'margin-left': 'auto',
            'margin-right': 'auto',
          }
        }
      >
        <Cell
          data-autobot-element-id={autobotId}
          isTouch={this.props.isTouch}
          isCurrentHoverDate={didUseKeyNavigation && currentDate === Number(hoveredDate)}
          selected={isSelected}
          grouped={isGrouped}
          valid={isValid}
          width={1}
        >
          {isCustomDayAvailable ? (
            customDayComponent
          ) : (
            <Text bold fontWeight='bold' fontSize={2} py={2}>
              {day}
            </Text>
          )}
        </Cell>
      </div>
    )
  }
}

export default Day
